import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateSeoPatternApplicationErrors,
  GetSeoPatternApplicationErrors,
  GetSeoPatternOptions,
  GetSeoPatternResponse,
  ListSeoPatternVariablesApplicationErrors,
  ListSeoPatternVariablesResponse,
  ListSeoPatternsResponse,
  ResetSeoPatternToDefaultApplicationErrors,
  ResetSeoPatternToDefaultOptions,
  ResetSeoPatternToDefaultResponse,
  SeoPattern,
  SetSeoPatternApplicationErrors,
  SetSeoPatternOptions,
  SetSeoPatternResponse,
  createSeoPattern as universalCreateSeoPattern,
  getSeoPattern as universalGetSeoPattern,
  listSeoPatternVariables as universalListSeoPatternVariables,
  listSeoPatterns as universalListSeoPatterns,
  resetSeoPatternToDefault as universalResetSeoPatternToDefault,
  setSeoPattern as universalSetSeoPattern,
} from './promote-seo-v1-seo-pattern-seo-patterns.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/seo' };

export function getSeoPattern(httpClient: HttpClient): GetSeoPatternSignature {
  return (pageType: string, options?: GetSeoPatternOptions) =>
    universalGetSeoPattern(
      pageType,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSeoPatternSignature {
  /**
   * Retrieves the SEO pattern in effect for a page type.
   *
   * The response contains the site's own pattern in `pattern`, and the pattern Wix
   * provides in `defaultPattern`. `source` reports which of the two is in effect.
   * `pattern` is empty when the site has no pattern of its own.
   * @param - Page type whose pattern to retrieve. Send a known page type name, such as
   * `STATIC_PAGE`, `BLOG_POST`, or `STORES_PRODUCT`.
   *
   * An unknown name fails with `UNSUPPORTED_PAGE_TYPE`. A known page type whose Wix
   * business solution isn't installed on the site fails with `PAGE_TYPE_NOT_ON_SITE`.
   *
   * See the list of [common page
   * types](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/introduction#page-types).
   */
  (pageType: string, options?: GetSeoPatternOptions): Promise<
    NonNullablePaths<
      GetSeoPatternResponse,
      | `seoPattern.pageType`
      | `seoPattern.pattern.tags`
      | `seoPattern.pattern.tags.${number}.type`
      | `seoPattern.pattern.tags.${number}.children`
      | `seoPattern.pattern.tags.${number}.custom`
      | `seoPattern.pattern.tags.${number}.disabled`
      | `seoPattern.source`
      | `seoPattern.origin`,
      4
    > & {
      __applicationErrorsType?: GetSeoPatternApplicationErrors;
    }
  >;
}

export function listSeoPatterns(
  httpClient: HttpClient
): ListSeoPatternsSignature {
  return () =>
    universalListSeoPatterns(
      // @ts-ignore
      { httpClient }
    );
}

interface ListSeoPatternsSignature {
  /**
   * Retrieves the patterns stored for a site, one per page type.
   *
   * Only returns page types that have had a pattern of the site's own stored, so a page
   * type that has never had one is absent. A page type that was reset to its default remains in the
   * list, with an empty `pattern` and `source` of `PATTERN_SOURCE_DEFAULT`.
   */
  (): Promise<
    NonNullablePaths<
      ListSeoPatternsResponse,
      | `seoPatterns`
      | `seoPatterns.${number}.pageType`
      | `seoPatterns.${number}.source`
      | `seoPatterns.${number}.origin`,
      2
    >
  >;
}

export function listSeoPatternVariables(
  httpClient: HttpClient
): ListSeoPatternVariablesSignature {
  return (pageType: string) =>
    universalListSeoPatternVariables(
      pageType,
      // @ts-ignore
      { httpClient }
    );
}

interface ListSeoPatternVariablesSignature {
  /**
   * Retrieves the variables a pattern for a page type may reference.
   *
   * Call this method to retrieve the valid variables for a page type before writing a
   * pattern.
   *
   * Variables for the page's own tags and variables for its structured data are returned
   * in separate fields, and a pattern may use either.
   * @param - Page type whose variables to retrieve. Send a known page type name, such as
   * `STATIC_PAGE`, `BLOG_POST`, or `STORES_PRODUCT`.
   *
   * An unknown name fails with `UNSUPPORTED_PAGE_TYPE`. A known page type whose Wix
   * business solution isn't installed on the site fails with `PAGE_TYPE_NOT_ON_SITE`.
   *
   * See the list of [common page
   * types](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/introduction#page-types).
   */
  (pageType: string): Promise<
    NonNullablePaths<
      ListSeoPatternVariablesResponse,
      | `variables`
      | `variables.${number}.key`
      | `variables.${number}.userVisible`
      | `structuredDataVariables`
      | `structuredDataVariables.${number}.key`
      | `structuredDataVariables.${number}.userVisible`,
      2
    > & {
      __applicationErrorsType?: ListSeoPatternVariablesApplicationErrors;
    }
  >;
}

export function createSeoPattern(
  httpClient: HttpClient
): CreateSeoPatternSignature {
  return (pageType: string, seoPattern: SeoPattern) =>
    universalCreateSeoPattern(
      pageType,
      seoPattern,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateSeoPatternSignature {
  /**
   * Creates a pattern for a page type that's still on its Wix default.
   *
   * The pattern must contain at least one tag. To leave a page type on its Wix default,
   * create nothing for it.
   *
   * To change a pattern that already exists, call [Set SEO
   * Pattern](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/set-seo-pattern).
   * @param - Page type to create a pattern for. Send a known page type name, such as
   * `STATIC_PAGE`, `BLOG_POST`, or `STORES_PRODUCT`.
   *
   * An unknown name fails with `UNSUPPORTED_PAGE_TYPE`. A known page type whose Wix
   * business solution isn't installed on the site fails with `PAGE_TYPE_NOT_ON_SITE`.
   *
   * See the list of [common page
   * types](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/introduction#page-types).
   * @param - Pattern to create for the page type.
   *
   * `pattern` is the only field to send. The page type comes from the request path, and
   * every other field is read-only. To create a pattern for a single page built from a
   * Wix Data collection, set `pageId`.
   * @returns The created pattern, with `source` reporting that the site's own pattern is now in
   * effect.
   */
  (pageType: string, seoPattern: SeoPattern): Promise<
    NonNullablePaths<
      SeoPattern,
      | `pageType`
      | `pattern.tags`
      | `pattern.tags.${number}.type`
      | `pattern.tags.${number}.children`
      | `pattern.tags.${number}.custom`
      | `pattern.tags.${number}.disabled`
      | `source`
      | `origin`,
      3
    > & {
      __applicationErrorsType?: CreateSeoPatternApplicationErrors;
    }
  >;
}

export function setSeoPattern(httpClient: HttpClient): SetSeoPatternSignature {
  return (
    pageType: string,
    seoPattern: SeoPattern,
    options?: NonNullablePaths<SetSeoPatternOptions, `fieldMask`, 0>
  ) =>
    universalSetSeoPattern(
      pageType,
      seoPattern,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetSeoPatternSignature {
  /**
   * Changes the pattern a site has for a page type.
   *
   * Setting `pattern` replaces the page type's tag templates in full, so retrieve the
   * pattern first and send back the complete set you want it to have. Sending an empty
   * pattern clears it, which returns the page type to its Wix default.
   *
   * This method changes a pattern that already exists rather than creating one. For a page
   * type still on its Wix default, call [Create SEO
   * Pattern](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/create-seo-pattern)
   * instead.
   *
   * A pattern has no revision, and the site's SEO settings in the dashboard write to the
   * same pattern. The last write wins, so retrieve the pattern immediately before writing
   * it.
   * @param - Page type whose pattern to change. Send a known page type name, such as
   * `STATIC_PAGE`, `BLOG_POST`, or `STORES_PRODUCT`.
   *
   * An unknown name fails with `UNSUPPORTED_PAGE_TYPE`.
   *
   * See the list of [common page
   * types](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/introduction#page-types).
   * @param - Pattern to set for the page type.
   *
   * `pattern` is the only writable field, and setting it replaces the page type's tag
   * templates in full. Sending an empty pattern clears it, which returns the page type to
   * its Wix default.
   */
  (
    pageType: string,
    seoPattern: SeoPattern,
    options?: NonNullablePaths<SetSeoPatternOptions, `fieldMask`, 0>
  ): Promise<
    NonNullablePaths<
      SetSeoPatternResponse,
      | `seoPattern.pageType`
      | `seoPattern.pattern.tags`
      | `seoPattern.pattern.tags.${number}.type`
      | `seoPattern.pattern.tags.${number}.children`
      | `seoPattern.pattern.tags.${number}.custom`
      | `seoPattern.pattern.tags.${number}.disabled`
      | `seoPattern.source`
      | `seoPattern.origin`,
      4
    > & {
      __applicationErrorsType?: SetSeoPatternApplicationErrors;
    }
  >;
}

export function resetSeoPatternToDefault(
  httpClient: HttpClient
): ResetSeoPatternToDefaultSignature {
  return (pageType: string, options?: ResetSeoPatternToDefaultOptions) =>
    universalResetSeoPatternToDefault(
      pageType,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ResetSeoPatternToDefaultSignature {
  /**
   * Removes the pattern a site has for a page type, so the Wix default applies again.
   *
   * The response reports the page type after the reset, with an empty `pattern` and
   * `source` of `PATTERN_SOURCE_DEFAULT`.
   * @param - Page type whose pattern to reset. Send a known page type name, such as
   * `STATIC_PAGE`, `BLOG_POST`, or `STORES_PRODUCT`.
   *
   * An unknown name fails with `UNSUPPORTED_PAGE_TYPE`.
   *
   * See the list of [common page
   * types](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/introduction#page-types).
   */
  (pageType: string, options?: ResetSeoPatternToDefaultOptions): Promise<
    NonNullablePaths<
      ResetSeoPatternToDefaultResponse,
      | `seoPattern.pageType`
      | `seoPattern.pattern.tags`
      | `seoPattern.pattern.tags.${number}.type`
      | `seoPattern.pattern.tags.${number}.children`
      | `seoPattern.pattern.tags.${number}.custom`
      | `seoPattern.pattern.tags.${number}.disabled`
      | `seoPattern.source`
      | `seoPattern.origin`,
      4
    > & {
      __applicationErrorsType?: ResetSeoPatternToDefaultApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  CreateSeoPatternRequest,
  CreateSeoPatternResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetSeoPatternOptions,
  GetSeoPatternRequest,
  GetSeoPatternResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListSeoPatternVariablesRequest,
  ListSeoPatternVariablesResponse,
  ListSeoPatternsRequest,
  ListSeoPatternsResponse,
  MessageEnvelope,
  Origin,
  PatternSource,
  ResetSeoPatternToDefaultOptions,
  ResetSeoPatternToDefaultRequest,
  ResetSeoPatternToDefaultResponse,
  RestoreInfo,
  SeoPattern,
  SeoPatternBlob,
  SeoPatternVariable,
  SetSeoPatternOptions,
  SetSeoPatternRequest,
  SetSeoPatternResponse,
  Tag,
  WebhookIdentityType,
} from './promote-seo-v1-seo-pattern-seo-patterns.universal.js';
